'use client';

import { Close } from '@mui/icons-material';
import { Box, Container, IconButton, List, Typography, useTheme } from '@mui/material';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';

import { ComponentSharedSiteBanner, Enum_Componentsharedsitebanner_Bannertype } from '@/types';

const DISMISSED_BANNERS_KEY = 'LEND_dismissed_banner';

function Banners({ banners }: { banners: ComponentSharedSiteBanner[] }) {
  const theme = useTheme();
  const [dismissedBannerIds, setDismissedBannerIds] = useState<string[]>([]);
  const [visibleBanners, setVisibleBanners] = useState<ComponentSharedSiteBanner[]>([]);

  // Load dismissed banner IDs from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(DISMISSED_BANNERS_KEY);
    if (stored) {
      try {
        const dismissedIds = JSON.parse(stored);
        setDismissedBannerIds(Array.isArray(dismissedIds) ? dismissedIds : []);
      } catch (error) {
        console.error('Failed to parse dismissed banners from localStorage:', error);
        setDismissedBannerIds([]);
      }
    }
  }, []);

  // Filter out dismissed banners whenever banners or dismissedBannerIds change
  useEffect(() => {
    const filtered = banners.filter((banner) => !dismissedBannerIds.includes(banner.id));
    setVisibleBanners(filtered);
  }, [banners, dismissedBannerIds]);

  const handleDismiss = useCallback(
    (bannerId: string) => {
      const newDismissedIds = [...dismissedBannerIds, bannerId];
      setDismissedBannerIds(newDismissedIds);

      // Save to localStorage
      try {
        localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify(newDismissedIds));
      } catch (error) {
        console.error('Failed to save dismissed banners to localStorage:', error);
      }
    },
    [dismissedBannerIds]
  );

  const getBannerColor = useCallback(
    (type: Enum_Componentsharedsitebanner_Bannertype) => {
      switch (type) {
        case Enum_Componentsharedsitebanner_Bannertype.Brand:
          return theme.palette.brand.brand;
        case Enum_Componentsharedsitebanner_Bannertype.Secondary:
          return theme.palette.brand.secondary;
        case Enum_Componentsharedsitebanner_Bannertype.Warning:
          return theme.palette.brand.error;
        default:
          return theme.palette.brand.primary;
      }
    },
    [theme]
  );

  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <>
      <List
        sx={{
          margin: 0,
          padding: 0,
        }}
      >
        {visibleBanners.map((banner, index) => (
          <li key={index}>
            <Box
              sx={{
                backgroundColor: getBannerColor(banner.BannerType),
                color: theme.palette.common.white,
                padding: { sx: 0, sm: 2 },
                paddingBlock: 2,
                position: 'relative',
              }}
            >
              <Container>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      columnGap: 1.5,
                      rowGap: 1,
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flex: 1,
                      strong: {
                        fontSize: '1.2rem',
                      },
                    }}
                  >
                    {banner?.MessageHeading && <strong>{banner.MessageHeading}</strong>}
                    {banner?.MessageBlurb && banner.MessageBlurb}
                    {banner?.BannerLink?.Url && (
                      <Link
                        href={banner?.BannerLink?.Url}
                        style={{
                          color: theme.palette.common.white,
                          textDecoration: 'underline',
                        }}
                      >
                        {banner?.BannerLink?.CustomLabel ?? 'Learn more'}
                      </Link>
                    )}
                  </Typography>

                  {banner.BannerDismissable && (
                    <IconButton
                      onClick={() => handleDismiss(banner.id)}
                      sx={{
                        color: theme.palette.common.white,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        flexShrink: 0,
                      }}
                      size="small"
                      aria-label="Dismiss banner"
                    >
                      <Close />
                    </IconButton>
                  )}
                </Box>
              </Container>
            </Box>
          </li>
        ))}
      </List>
    </>
  );
}

export default Banners;
